import React, { useEffect, useRef, useState } from "react";
import type { User, FileRecord } from "../shared/contracts.ts";
import {
  demandDocumentState,
  type DemandConfig,
  type DemandGrant,
  type DemandInput,
  type DemandRecord,
  type Citizen,
} from "../shared/demands.ts";
import {
  api,
  Banner,
  Button as BaseButton,
  Field,
  Modal,
  date,
} from "./ui.tsx";
const Button = (props: React.ComponentProps<typeof BaseButton>) => (
  <BaseButton type="button" {...props} />
);
import { PDFPreview } from "./pdf-preview.tsx";
import "./demands.css";
import { queueDemand } from "./offline-store.ts";
import type { OfflinePolicy } from "../server/offline.ts";
const blank = (): DemandInput => ({
  citizen: { name: "", phone: "", address: "" },
  typeId: "rg",
  subtypeId: null,
  note: "",
  priority: "Baixa",
  deadline: null,
  deadlineTime: null,
  attachmentIds: [],
});
type Access = {
  grant: DemandGrant;
  types: DemandConfig["types"];
  users: { id: string; name: string }[];
};
type Filters = {
  search: string;
  area: string;
  type: string;
  subtype: string;
  assessor: string;
  priority: string;
  status: string;
  sort: string;
  offset: number;
};
const initialFilters: Filters = {
  search: "",
  area: "active",
  type: "",
  subtype: "",
  assessor: "",
  priority: "",
  status: "",
  sort: "newest",
  offset: 0,
};
const areas = {
  active: "Ativas",
  awaiting_acceptance: "Aguardando aceite",
  closed: "Finalizadas",
  trash: "Lixeira",
};
type Detail = { record: DemandRecord; files: FileRecord[] };
const fileSource = (id: string) =>
  "/api/demands/files/" + encodeURIComponent(id);
export function Demands({
  user,
  active,
  onDirty,
  notify,
}: {
  user: User;
  active: boolean;
  onDirty: (v: boolean) => void;
  notify: (s: string) => void;
}) {
  const [access, setAccess] = useState<Access | null>(null),
    [query, setQuery] = useState(initialFilters),
    [draftFilters, setDraftFilters] = useState(initialFilters),
    [filterOpen, setFilterOpen] = useState(false),
    [page, setPage] = useState<any>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [refresh, setRefresh] = useState(0),
    [creating, setCreating] = useState(false),
    [value, setValue] = useState<DemandInput>(blank),
    [step, setStep] = useState(0),
    [files, setFiles] = useState<FileRecord[]>([]),
    [busy, setBusy] = useState(false),
    [detail, setDetail] = useState<Detail | null>(null),
    [preview, setPreview] = useState<FileRecord | null>(null),
    [finish, setFinish] = useState<0 | 1 | 2>(0),
    [finishNote, setFinishNote] = useState(""),
    [editing, setEditing] = useState(false),
    [participants, setParticipants] = useState(false),
    [responsible, setResponsible] = useState(""),
    [collaborators, setCollaborators] = useState<string[]>([]),
    [admin, setAdmin] = useState(false),
    [citizens, setCitizens] = useState<Citizen[]>([]),
    [acceptance, setAcceptance] = useState(0);
  const operation = useRef(crypto.randomUUID()),
    formKey = useRef(""),
    returnFocus = useRef<HTMLElement | null>(null);
  const [offlinePolicy,setOfflinePolicy]=useState<OfflinePolicy|null>(null);
  const [networkFailed,setNetworkFailed]=useState(false);
  useEffect(()=>{api<OfflinePolicy>("/offline/policy").then(setOfflinePolicy).catch(()=>{});},[user.id]);
  useEffect(() => {
    const key = JSON.stringify(value);
    if (formKey.current !== key) {
      formKey.current = key;
      operation.current = crypto.randomUUID();
    }
  }, [value]);
  useEffect(() => {
    const openAcceptance = () =>
      setQuery({
        ...initialFilters,
        area: "awaiting_acceptance",
        assessor: user.id,
      });
    window.addEventListener("demand-acceptance-open", openAcceptance);
    const openRecord = (event: Event) => {
      const id = (event as CustomEvent).detail;
      if (typeof id === "string" && /^[a-f0-9-]{36}$/i.test(id)) void open(id);
    };
    window.addEventListener("demand-record-open", openRecord);
    return () => {
      window.removeEventListener("demand-acceptance-open", openAcceptance);
      window.removeEventListener("demand-record-open", openRecord);
    };
  }, [user.id]);
  useEffect(() => {
    if (!active) return;
    const a = new AbortController();
    api<Access>("/demands/access", "GET", undefined, a.signal)
      .then(setAccess)
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      });
    return () => a.abort();
  }, [active, refresh]);
  useEffect(() => {
    if (!active || !access?.grant.access) {
      setLoading(false);
      return;
    }
    const a = new AbortController();
    setLoading(true);
    setError("");
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(query))
      if (v !== "" && v !== undefined) p.set(k, String(v));
    api("/demands?" + p, "GET", undefined, a.signal)
      .then(setPage)
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!a.signal.aborted) setLoading(false);
      });
    api<any>(
      "/demands?area=awaiting_acceptance&limit=1&assessor=" +
        encodeURIComponent(user.id),
      "GET",
      undefined,
      a.signal,
    )
      .then((p) => setAcceptance(p.total))
      .catch(() => {});
    return () => a.abort();
  }, [active, access, query, refresh, user.id]);
  const type = access?.types.find((t) => t.id === value.typeId),
    detailType = access?.types.find((t) => t.id === detail?.record.typeId);
  const canEdit =
    !!detail &&
    (user.admin ||
      detail.record.responsibleId === user.id ||
      detail.record.collaboratorIds.includes(user.id));
  const changed = (patch: Partial<DemandInput>) => {
    setValue((v) => ({ ...v, ...patch }));
    onDirty(true);
  };
  const selectedCount =
    ["type", "subtype", "assessor", "priority", "status"].filter(
      (k) => !!query[k as keyof Filters],
    ).length + (query.sort !== "newest" ? 1 : 0);
  async function open(id: string, target?: HTMLElement) {
    try {
      setError("");
      returnFocus.current = target ?? null;
      setDetail(await api<Detail>("/demands/" + id));
    } catch (e: any) {
      setError(e.message);
    }
  }
  function closeDetail() {
    setDetail(null);
    setEditing(false);
    setParticipants(false);
    onDirty(false);
    requestAnimationFrame(() =>
      returnFocus.current?.focus({ preventScroll: true }),
    );
  }
  async function command(action: string, extra: any = {}) {
    if (!detail) return false;
    setBusy(true);
    setError("");
    try {
      const d = await api<DemandRecord>(
        "/demands/" + detail.record.id + "/commands",
        "POST",
        {
          operationId: operation.current,
          version: detail.record.version,
          action,
          ...extra,
        },
      );
      operation.current = crypto.randomUUID();
      setRefresh((n) => n + 1);
      setFinish(0);
      setEditing(false);
      onDirty(false);
      setParticipants(false);
      if (
        action === "participants" &&
        d.responsibleId !== user.id &&
        !d.collaboratorIds.includes(user.id) &&
        !user.admin
      ) {
        closeDetail();
      } else if (
        ["complete", "reopen", "trash", "restore", "accept"].includes(action)
      ) {
        closeDetail();
      } else setDetail(await api<Detail>("/demands/" + d.id));
      notify(
        (
          {
            complete: "Demanda encerrada",
            reopen: "Demanda reaberta",
            trash: "Demanda enviada à lixeira",
            restore: "Demanda restaurada",
            accept: "Demanda aceita",
            edit: "Alterações salvas",
            participants: "Participantes atualizados",
            document: "Documento conferido",
            attach: "Documento anexado",
          } as any
        )[action] + " no servidor.",
      );
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function upload(input: HTMLInputElement, inDetail = false) {
    const f = input.files?.[0];
    if (!f) return;
    setBusy(true);
    setError("");
    try {
      const data = new FormData();
      data.set("file", f);
      const saved = await api<FileRecord>("/demands/files", "POST", data);
      if (inDetail && detail) {
        if (!(await command("attach", { fileId: saved.id }))) return;
      } else {
        setFiles((fs) => [...fs, saved]);
        changed({ attachmentIds: [...value.attachmentIds, saved.id] });
      }
      notify("Documento recebido no servidor. Confira o conteúdo e a leitura.");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
      input.value = "";
    }
  }
  async function register(extra: Partial<DemandInput> = {}) {
    setNetworkFailed(false);
    setBusy(true);
    setError("");
    try {
      const v = { ...value, ...extra };
      const saved = await api<DemandRecord>("/demands", "POST", {
        operationId: operation.current,
        value: v,
      });
      setCreating(false);
      setStep(0);
      setValue(blank());
      setFiles([]);
      setCitizens([]);
      onDirty(false);
      setQuery((q) => ({ ...q, area: "active", sort: "newest", offset: 0 }));
      setRefresh((n) => n + 1);
      notify(
        saved.area === "awaiting_acceptance"
          ? "Solicitação enviada. Aguardando aceite do assessor."
          : "Demanda criada no servidor." +
              (query.search || selectedCount
                ? " Os filtros mantidos podem ocultá-la na lista."
                : ""),
      );
    } catch (e: any) {
      if (e.code === "CITIZEN_CHOICE") {
        try {
          setCitizens(
            await api<Citizen[]>(
              "/demands/citizens?name=" +
                encodeURIComponent(value.citizen.name) +
                "&phone=" +
                encodeURIComponent(value.citizen.phone),
            ),
          );
        } catch (problem: any) {
          setError(problem.message);
        }
      } else if (
        e.code === "DEMAND_DUPLICATE" &&
        window.confirm(
          e.message + " Registrar uma nova solicitação mesmo assim?",
        )
      ) {
        setBusy(false);
        return register({ confirmDuplicate: true });
      }
      setError(e.message);
      if(e.code === "NETWORK")setNetworkFailed(true);
    } finally {
      setBusy(false);
    }
  }
  const citizenFields = (
    <>
      <Field label="Nome do cidadão">
        <input
          required
          value={value.citizen.name}
          onChange={(e) =>
            changed({ citizen: { ...value.citizen, name: e.target.value } })
          }
          autoComplete="name"
        />
      </Field>
      <Field label="Telefone">
        <input
          type="tel"
          value={value.citizen.phone}
          onChange={(e) =>
            changed({ citizen: { ...value.citizen, phone: e.target.value } })
          }
          autoComplete="tel"
        />
      </Field>
      <Field label="Endereço">
        <input
          value={value.citizen.address}
          onChange={(e) =>
            changed({ citizen: { ...value.citizen, address: e.target.value } })
          }
          autoComplete="street-address"
        />
      </Field>
      <Field label="Observação">
        <textarea
          value={value.note}
          onChange={(e) => changed({ note: e.target.value })}
        />
      </Field>
    </>
  );
  const priorityFields = (
    <>
      <Field label="Prioridade">
        <select
          value={value.priority}
          onChange={(e) =>
            changed({ priority: e.target.value as DemandRecord["priority"] })
          }
        >
          {["Baixa", "Média", "Alta"].map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
      </Field>
      {value.deadline === null ? (
        <Button onClick={() => changed({ deadline: "" })}>
          Adicionar prazo
        </Button>
      ) : (
        <section className="demand-deadline">
          <Field label="Data do prazo">
            <input
              type="date"
              value={value.deadline ?? ""}
              onChange={(e) =>
                changed({
                  deadline: e.target.value,
                  deadlineTime: e.target.value ? value.deadlineTime : null,
                })
              }
            />
          </Field>
          <Field label="Horário (opcional)">
            <input
              type="time"
              value={value.deadlineTime ?? ""}
              onChange={(e) => changed({ deadlineTime: e.target.value })}
            />
          </Field>
          <Button
            onClick={() => changed({ deadline: null, deadlineTime: null })}
          >
            Remover prazo
          </Button>
        </section>
      )}
    </>
  );
  if (!active) return null;
  return (
    <section className="demands-module">
      <div className="section-title">
        <h1>Demandas</h1>
        {user.admin && (
          <details className="module-options">
            <summary>Opções</summary>
            <Button onClick={() => setAdmin(true)}>
              Configurações de Demandas
            </Button>
          </details>
        )}
      </div>
      {error && (
        <Banner kind="danger">
          {error}{" "}
          <Button onClick={() => setRefresh((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      {!access ? (
        <p role="status">Carregando acesso…</p>
      ) : !access.grant.access ? (
        <Banner>
          O administrador ainda não habilitou seu acesso a Demandas.
        </Banner>
      ) : (
        <>
          <div className="demand-primary">
            <Field label="Buscar por nome">
              <input
                type="search"
                value={query.search}
                onChange={(e) =>
                  setQuery((q) => ({ ...q, search: e.target.value, offset: 0 }))
                }
              />
            </Field>
            {access.grant.create && (
              <Button
                kind="primary"
                onClick={() => {
                  if (
                    !access.types.some((t) => t.active && t.id === value.typeId)
                  )
                    setValue((v) => ({
                      ...v,
                      typeId: access.types.find((t) => t.active)?.id ?? "",
                      subtypeId: null,
                    }));
                  setCreating(true);
                  operation.current = crypto.randomUUID();
                }}
              >
                Registrar Demanda
              </Button>
            )}
          </div>
          <div className="demand-area">
            <Field label="Área de demandas">
              <select
                value={query.area}
                onChange={(e) =>
                  setQuery((q) => ({ ...q, area: e.target.value, offset: 0 }))
                }
              >
                {Object.entries(areas).map(([id, label]) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Button
              onClick={() => {
                setDraftFilters(query);
                setFilterOpen(true);
              }}
            >
              Filtrar{selectedCount ? " · " + selectedCount : ""}
            </Button>
          </div>
          {acceptance > 0 && query.area !== "awaiting_acceptance" && (
            <Button
              className="demand-acceptance"
              onClick={() =>
                setQuery({
                  ...initialFilters,
                  area: "awaiting_acceptance",
                  assessor: user.id,
                })
              }
            >
              {acceptance}{" "}
              {acceptance === 1
                ? "demanda aguardando seu aceite"
                : "demandas aguardando seu aceite"}
            </Button>
          )}
          {!!selectedCount && (
            <div className="demand-chips">
              {["type", "subtype", "assessor", "priority", "status"]
                .filter((k) => query[k as keyof Filters])
                .map((k) => (
                  <Button
                    key={k}
                    onClick={() =>
                      setQuery((q) => ({
                        ...q,
                        [k]: "",
                        ...(k === "type" ? { subtype: "" } : {}),
                        offset: 0,
                      }))
                    }
                  >
                    {k === "type"
                      ? access.types.find((t) => t.id === query.type)?.name
                      : k === "assessor"
                        ? access.users.find((u) => u.id === query.assessor)
                            ?.name
                        : query[k as keyof Filters]}{" "}
                    ×
                  </Button>
                ))}
              <Button
                onClick={() =>
                  setQuery((q) => ({
                    ...initialFilters,
                    area: q.area,
                    search: q.search,
                  }))
                }
              >
                Limpar filtros
              </Button>
            </div>
          )}
          {loading ? (
            <p role="status">Buscando demandas…</p>
          ) : (
            page && (
              <>
                <p className="muted">
                  {page.total} {page.total === 1 ? "demanda" : "demandas"} ·{" "}
                  {(areas as any)[query.area]}
                </p>
                <div className="demand-records">
                  {page.rows.map((d: any) => (
                    <button
                      className="demand-record"
                      key={d.id}
                      onClick={(e) => open(d.id, e.currentTarget)}
                    >
                      <strong>{d.citizen.name}</strong>
                      <span>
                        {access.types.find((t) => t.id === d.typeId)?.name ??
                          d.typeId}{" "}
                        · {d.status}
                      </span>
                      <small>
                        {d.priority === "Alta" ? "Alta prioridade · " : ""}
                        {d.deadline
                          ? "Prazo: " +
                            d.deadline.split("-").reverse().join("/") +
                            (d.deadlineTime ? " · " + d.deadlineTime : "")
                          : ""}
                      </small>
                      <small>{d.documents.label}</small>
                      <span className="demand-arrow" aria-hidden="true">
                        ›
                      </span>
                    </button>
                  ))}
                </div>
                {!page.total && (
                  <Banner>
                    Nenhuma demanda encontrada.{" "}
                    {query.search || selectedCount
                      ? "Ajuste a busca ou os filtros."
                      : "Os cadastros autorizados aparecerão aqui."}
                  </Banner>
                )}
                {page.total > 20 && (
                  <div className="actions">
                    <Button
                      disabled={!query.offset}
                      onClick={() =>
                        setQuery((q) => ({
                          ...q,
                          offset: Math.max(0, q.offset - 20),
                        }))
                      }
                    >
                      Anterior
                    </Button>
                    <span>
                      {query.offset + 1}–
                      {Math.min(page.total, query.offset + 20)} de {page.total}
                    </span>
                    <Button
                      disabled={query.offset + 20 >= page.total}
                      onClick={() =>
                        setQuery((q) => ({ ...q, offset: q.offset + 20 }))
                      }
                    >
                      Próximas
                    </Button>
                  </div>
                )}
              </>
            )
          )}
        </>
      )}
      <details>
        <summary>Conexões e disponibilidade</summary>
        <p>
          Esta área salva no banco do Gabinete Central. Google Sheets/Drive,
          leitura por IA e envio de notificações push ainda exigem a conexão dos
          serviços. Operações sem internet não são confirmadas; mantenha o
          formulário aberto para tentar novamente.
        </p>
      </details>
      {filterOpen && access && (
        <Modal
          title="Filtrar demandas"
          mobileFullScreen
          onClose={() => setFilterOpen(false)}
        >
          <Field label="Tipo">
            <select
              value={draftFilters.type}
              onChange={(e) =>
                setDraftFilters({
                  ...draftFilters,
                  type: e.target.value,
                  subtype: "",
                })
              }
            >
              <option value="">Todos</option>
              {access.types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {!!access.types
            .find((t) => t.id === draftFilters.type)
            ?.subtypes.filter((s) => s.active).length && (
            <Field label="Subtipo">
              <select
                value={draftFilters.subtype}
                onChange={(e) =>
                  setDraftFilters({ ...draftFilters, subtype: e.target.value })
                }
              >
                <option value="">Todos</option>
                {access.types
                  .find((t) => t.id === draftFilters.type)
                  ?.subtypes.filter((s) => s.active)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          {(user.admin ||
            access.grant.readAll ||
            access.grant.profile === "councillor") && (
            <Field label="Assessor">
              <select
                value={draftFilters.assessor}
                onChange={(e) =>
                  setDraftFilters({ ...draftFilters, assessor: e.target.value })
                }
              >
                <option value="">Todos no seu acesso</option>
                {access.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Prioridade">
            <select
              value={draftFilters.priority}
              onChange={(e) =>
                setDraftFilters({ ...draftFilters, priority: e.target.value })
              }
            >
              <option value="">Todas</option>
              {["Baixa", "Média", "Alta"].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </Field>
          <Field label="Situação">
            <select
              value={draftFilters.status}
              onChange={(e) =>
                setDraftFilters({ ...draftFilters, status: e.target.value })
              }
            >
              <option value="">Todas</option>
              {[...new Set(access.types.flatMap((t) => t.statuses))].map(
                (s) => (
                  <option key={s}>{s}</option>
                ),
              )}
            </select>
          </Field>
          <Field label="Ordenar por">
            <select
              value={draftFilters.sort}
              onChange={(e) =>
                setDraftFilters({ ...draftFilters, sort: e.target.value })
              }
            >
              <option value="newest">Mais recentes</option>
              <option value="oldest">Mais antigas</option>
              <option value="priority">Prioridade</option>
            </select>
          </Field>
          <div className="actions">
            <Button
              onClick={() =>
                setDraftFilters({
                  ...initialFilters,
                  area: query.area,
                  search: query.search,
                })
              }
            >
              Limpar
            </Button>
            <Button
              kind="primary"
              onClick={() => {
                setQuery({ ...draftFilters, offset: 0 });
                setFilterOpen(false);
              }}
            >
              Aplicar filtros
            </Button>
          </div>
        </Modal>
      )}
      {creating && access && (
        <Modal
          title="Registrar Demanda"
          mobileFullScreen
          className="guided-form-dialog"
          onClose={() => {
            setCreating(false);
            onDirty(false);
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 2) void register();
              else setStep((s) => s + 1);
            }}
          >
            <fieldset disabled={busy}>
              <div className="guided-form-body">
                <p className="muted">
                  {step + 1} de 3 ·{" "}
                  {["Tipo", "Documentos", "Conferência"][step]}
                </p>
                {error && <Banner kind="danger">{error}</Banner>}
                {step===2&&networkFailed&&offlinePolicy?.enabled&&<Button onClick={async()=>{
                  try{await queueDemand(user.id,{operationId:operation.current,value},offlinePolicy.retentionHours);setCreating(false);setStep(0);setValue(blank());setFiles([]);onDirty(false);notify("Salvo no dispositivo. Ainda não foi registrado no servidor. Envie em Meu perfil → Pendências offline quando houver conexão.");}catch(e){setError((e as Error).message);}
                }}>Salvar pendência neste aparelho</Button>}
                {step === 0 && (
                  <>
                    <Field label="Tipo de demanda">
                      <select
                        value={value.typeId}
                        onChange={(e) =>
                          changed({ typeId: e.target.value, subtypeId: null })
                        }
                      >
                        {access.types
                          .filter((t) => t.active)
                          .map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                      </select>
                    </Field>
                    {!!type?.subtypes.some((s) => s.active) && (
                      <Field
                        label={
                          "Subtipo" +
                          (type.subtypeRequired ? " · obrigatório" : "")
                        }
                      >
                        <select
                          required={type.subtypeRequired}
                          value={value.subtypeId ?? ""}
                          onChange={(e) =>
                            changed({ subtypeId: e.target.value || null })
                          }
                        >
                          <option value="">Selecione</option>
                          {type.subtypes
                            .filter((s) => s.active)
                            .map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                        </select>
                      </Field>
                    )}
                  </>
                )}
                {step === 1 && (
                  <>
                    <label className="button demand-upload">
                      Adicionar documentos
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp"
                        onChange={(e) => upload(e.currentTarget)}
                      />
                    </label>
                    {busy && (
                      <p role="status">
                        Recebendo e lendo documento no servidor…
                      </p>
                    )}
                    {files.map((f) => (
                      <article className="demand-file" key={f.id}>
                        <strong>{f.name}</strong>
                        <p>
                          {f.extractionState === "ready"
                            ? "Texto extraído. Confira antes de utilizar."
                            : f.extractionMessage}
                        </p>
                        <Button onClick={() => setPreview(f)}>Consultar</Button>
                        <Button
                          onClick={() => {
                            setFiles((fs) => fs.filter((x) => x.id !== f.id));
                            changed({
                              attachmentIds: value.attachmentIds.filter(
                                (id) => id !== f.id,
                              ),
                            });
                          }}
                        >
                          Retirar
                        </Button>
                      </article>
                    ))}
                    {!files.length && (
                      <p>
                        Nenhum documento adicionado. Você pode continuar e
                        complementar depois.
                      </p>
                    )}
                    <p className="muted">
                      Os arquivos são armazenados no servidor. A extração de
                      texto disponível é convencional; IA e OCR ainda precisam
                      de conexão e configuração.
                    </p>
                  </>
                )}
                {step === 2 && (
                  <>
                    {citizenFields}
                    {priorityFields}
                    {(user.admin ||
                      access.grant.send ||
                      access.grant.profile === "councillor") && (
                      <Field label="Responsável">
                        <select
                          required
                          value={
                            value.responsibleId ??
                            (access.grant.profile === "councillor"
                              ? ""
                              : user.id)
                          }
                          onChange={(e) =>
                            changed({ responsibleId: e.target.value })
                          }
                        >
                          <option value="">Selecione o responsável</option>
                          {access.users
                            .filter(
                              (u) =>
                                access.grant.profile !== "councillor" ||
                                u.id !== user.id,
                            )
                            .map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                        </select>
                      </Field>
                    )}
                    {citizens.length > 0 && (
                      <section>
                        <h3>Cadastros encontrados</h3>
                        <p>Escolha o cidadão ou confirme que é outra pessoa.</p>
                        {citizens.map((c) => (
                          <Button
                            key={c.id}
                            onClick={() => {
                              changed({ citizenId: c.id });
                              setCitizens([]);
                              setError("");
                            }}
                          >
                            {c.name} · {c.phone} · {c.address}
                          </Button>
                        ))}
                        <Button
                          onClick={() => {
                            changed({ confirmNewCitizen: true });
                            setCitizens([]);
                            setError("");
                          }}
                        >
                          É outra pessoa
                        </Button>
                      </section>
                    )}
                  </>
                )}
              </div>
              <div className="guided-form-actions actions">
                <Button
                  type="button"
                  onClick={() =>
                    step ? setStep((s) => s - 1) : setCreating(false)
                  }
                >
                  {step ? "Voltar" : "Cancelar"}
                </Button>
                <Button kind="primary" type="submit" busy={busy}>
                  {step === 2 ? "Registrar Demanda" : "Continuar"}
                </Button>
              </div>
            </fieldset>
          </form>
        </Modal>
      )}
      {detail && access && (
        <Modal
          title={detail.record.citizen.name}
          mobileFullScreen
          onClose={closeDetail}
        >
          {error && <Banner kind="danger">{error}</Banner>}
          <p>
            {detailType?.name} · {detail.record.status} ·{" "}
            {detail.record.priority}
          </p>
          <p>
            Responsável:{" "}
            {access.users.find((u) => u.id === detail.record.responsibleId)
              ?.name ?? "Usuário fora da lista ativa"}
          </p>
          <p>Telefone: {detail.record.citizen.phone || "Não informado"}</p>
          <p>Endereço: {detail.record.citizen.address || "Não informado"}</p>
          <p className="demand-preserve">
            {detail.record.note || "Sem observação."}
          </p>
          {detail.record.deadline && (
            <p>
              Prazo: {detail.record.deadline.split("-").reverse().join("/")}{" "}
              {detail.record.deadlineTime}
            </p>
          )}
          <details>
            <summary>
              Documentos ·{" "}
              {demandDocumentState(detail.record, detailType).label}
            </summary>
            {detail.files.map((f) => (
              <article className="demand-file" key={f.id}>
                <strong>{f.name}</strong>
                <Button onClick={() => setPreview(f)}>Consultar</Button>
                {canEdit && access.grant.edit && detailType?.documents && (
                  <>
                    <Field label="Tipo de documento">
                      <select
                        value={
                          detail.record.attachments.find(
                            (a) => a.fileId === f.id,
                          )?.documentType ?? ""
                        }
                        onChange={(e) =>
                          void command("document", {
                            fileId: f.id,
                            documentType: e.target.value || null,
                            validated: false,
                          })
                        }
                      >
                        <option value="">Não classificado</option>
                        {detailType.documents.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <label>
                      <input
                        type="checkbox"
                        checked={
                          detail.record.attachments.find(
                            (a) => a.fileId === f.id,
                          )?.validated ?? false
                        }
                        onChange={(e) =>
                          void command("document", {
                            fileId: f.id,
                            documentType:
                              detail.record.attachments.find(
                                (a) => a.fileId === f.id,
                              )?.documentType ?? null,
                            validated: e.target.checked,
                          })
                        }
                      />{" "}
                      Documento conferido e válido
                    </label>
                  </>
                )}
              </article>
            ))}
            {canEdit && access.grant.edit && (
              <label className="button demand-upload">
                Adicionar documento
                <input
                  disabled={busy}
                  type="file"
                  accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => upload(e.currentTarget, true)}
                />
              </label>
            )}
          </details>
          {detail.record.firstClosedAt && (
            <p>
              Primeiro encerramento: {date(detail.record.firstClosedAt)}.
              Reabrir e encerrar novamente não concede outra recompensa.
            </p>
          )}
          {detail.record.rewardState === "pending_configuration" && (
            <Banner>
              O encerramento foi registrado. A recompensa aguarda configuração
              administrativa, sem ganho confirmado.
            </Banner>
          )}
          <details>
            <summary>Histórico desta demanda</summary>
            {detail.record.history.map((h) => (
              <p key={h.id}>
                {date(h.at)} · {h.text}
              </p>
            ))}
          </details>
          <div className="actions">
            {detail.record.area === "awaiting_acceptance" &&
              detail.record.responsibleId === user.id && (
                <Button
                  kind="primary"
                  busy={busy}
                  onClick={() => void command("accept")}
                >
                  Aceitar demanda
                </Button>
              )}
            {canEdit &&
              access.grant.complete &&
              detail.record.area === "active" && (
                <Button
                  kind="primary"
                  onClick={() => {
                    operation.current = crypto.randomUUID();
                    setFinish(1);
                    setFinishNote("");
                  }}
                >
                  Feito
                </Button>
              )}
            {canEdit &&
              access.grant.complete &&
              detail.record.area === "closed" && (
                <Button busy={busy} onClick={() => void command("reopen")}>
                  Reabrir demanda
                </Button>
              )}
            {canEdit &&
              access.grant.edit &&
              detail.record.area === "active" && (
                <Button
                  onClick={() => {
                    setValue({
                      citizen: detail.record.citizen,
                      citizenId: detail.record.citizenId,
                      typeId: detail.record.typeId,
                      subtypeId: detail.record.subtypeId,
                      note: detail.record.note,
                      priority: detail.record.priority,
                      deadline: detail.record.deadline,
                      deadlineTime: detail.record.deadlineTime,
                      attachmentIds: detail.record.attachments.map(
                        (a) => a.fileId,
                      ),
                    });
                    setEditing(true);
                  }}
                >
                  Editar
                </Button>
              )}
            {canEdit &&
              (access.grant.share || access.grant.transfer) &&
              detail.record.area === "active" && (
                <Button
                  onClick={() => {
                    setResponsible(detail.record.responsibleId);
                    setCollaborators(detail.record.collaboratorIds);
                    operation.current = crypto.randomUUID();
                    setParticipants(true);
                  }}
                >
                  Responsável e colaboradores
                </Button>
              )}
            {(user.admin ||
              (detail.record.responsibleId === user.id &&
                access.grant.profile !== "councillor")) && (
              <Button
                busy={busy}
                onClick={() => {
                  if (
                    detail.record.area === "trash" ||
                    window.confirm(
                      "Enviar esta demanda à lixeira por dez dias?",
                    )
                  )
                    void command(
                      detail.record.area === "trash" ? "restore" : "trash",
                    );
                }}
              >
                {detail.record.area === "trash"
                  ? "Restaurar demanda"
                  : "Excluir demanda"}
              </Button>
            )}
          </div>
        </Modal>
      )}
      {finish > 0 && (
        <Modal
          title="Encerrar demanda?"
          mobileFullScreen
          onClose={() => setFinish(0)}
        >
          {error && <Banner kind="danger">{error}</Banner>}
          {finish === 1 ? (
            <>
              <p>Confirma que o atendimento foi realizado?</p>
              <div className="actions">
                <Button onClick={() => setFinish(0)}>Não</Button>
                <Button kind="primary" onClick={() => setFinish(2)}>
                  Sim
                </Button>
              </div>
            </>
          ) : (
            <>
              <Field label="Observação de encerramento (opcional)">
                <textarea
                  value={finishNote}
                  onChange={(e) => {
                    setFinishNote(e.target.value);
                    operation.current = crypto.randomUUID();
                  }}
                />
              </Field>
              <Button
                kind="primary"
                busy={busy}
                onClick={() =>
                  void command("complete", {
                    confirmed: true,
                    note: finishNote,
                  })
                }
              >
                Confirmar conclusão
              </Button>
            </>
          )}
        </Modal>
      )}
      {editing && (
        <Modal
          title="Editar demanda"
          mobileFullScreen
          onClose={() => setEditing(false)}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void command("edit", { value });
            }}
          >
            {citizenFields}
            {priorityFields}
            <Button kind="primary" type="submit" busy={busy}>
              Salvar alterações
            </Button>
          </form>
        </Modal>
      )}
      {participants && access && (
        <Modal
          title="Responsável e colaboradores"
          mobileFullScreen
          onClose={() => setParticipants(false)}
        >
          {access.grant.transfer && (
            <Field label="Responsável atual">
              <select
                value={responsible}
                onChange={(e) => {
                  setResponsible(e.target.value);
                  operation.current = crypto.randomUUID();
                }}
              >
                {access.users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {access.grant.share && (
            <fieldset>
              <legend>Colaboradores</legend>
              {access.users
                .filter((u) => u.id !== responsible)
                .map((u) => (
                  <label className="demand-user-option" key={u.id}>
                    <input
                      type="checkbox"
                      checked={collaborators.includes(u.id)}
                      onChange={(e) => {
                        setCollaborators((c) =>
                          e.target.checked
                            ? [...c, u.id]
                            : c.filter((id) => id !== u.id),
                        );
                        operation.current = crypto.randomUUID();
                      }}
                    />
                    {u.name}
                  </label>
                ))}
            </fieldset>
          )}
          <p>
            A transferência muda o dono para o responsável atual. O criador e
            recompensas anteriores permanecem registrados.
          </p>
          <Button
            kind="primary"
            busy={busy}
            onClick={() =>
              void command("participants", {
                responsibleId: responsible,
                collaboratorIds: collaborators,
              })
            }
          >
            Salvar participantes
          </Button>
        </Modal>
      )}
      {preview && (
        <Modal
          title={preview.name}
          mobileFullScreen
          onClose={() => setPreview(null)}
        >
          {preview.mime === "application/pdf" ? (
            <PDFPreview
              name={preview.name}
              sourceUrl={fileSource(preview.id)}
            />
          ) : preview.mime.startsWith("image/") ? (
            <img
              className="demand-image"
              src={fileSource(preview.id)}
              alt={"Documento " + preview.name}
            />
          ) : (
            <p>Consulte o texto extraído ou baixe o documento.</p>
          )}
          {preview.extractedText && (
            <details>
              <summary>Texto extraído · conferir</summary>
              <pre className="demand-preserve">{preview.extractedText}</pre>
            </details>
          )}
          <a className="button" href={fileSource(preview.id) + "?download=1"}>
            Baixar documento
          </a>
        </Modal>
      )}
      {admin && (
        <DemandAdmin
          onClose={() => {
            setAdmin(false);
            setRefresh((n) => n + 1);
          }}
        />
      )}
    </section>
  );
}
const grantNames = {
  access: "Acessar Demandas",
  create: "Registrar demanda",
  edit: "Editar demandas autorizadas",
  complete: "Encerrar e reabrir",
  share: "Gerenciar colaboradores",
  transfer: "Transferir responsabilidade",
  send: "Enviar a outro assessor",
  readAll: "Consultar todas as demandas",
};
function DemandAdmin({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<any>(),
    [config, setConfig] = useState<DemandConfig | null>(null),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    const d = await api("/demands/settings");
    setData(d);
    setConfig(d.draft?.config ?? d.published.config);
  }
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, []);
  async function save(publish: boolean) {
    setBusy(true);
    setError("");
    try {
      await api(
        "/demands/settings/" + (publish ? "publish" : "draft"),
        publish ? "POST" : "PUT",
        {
          revision: Math.max(
            data.published.revision,
            data.draft?.revision ?? 0,
          ),
          config,
        },
      );
      await load();
      setMessage(
        publish
          ? "Configuração publicada."
          : "Rascunho salvo; permissões não foram alteradas.",
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  function typeChange(index: number, patch: any) {
    setConfig(
      (c) =>
        c && {
          ...c,
          types: c.types.map((t, i) => (i === index ? { ...t, ...patch } : t)),
        },
    );
  }
  return (
    <Modal title="Configurações de Demandas" mobileFullScreen onClose={onClose}>
      {error && <Banner kind="danger">{error}</Banner>}
      {message && <Banner>{message}</Banner>}
      {!config || !data ? (
        <p role="status">Carregando configuração…</p>
      ) : (
        <fieldset disabled={busy}>
          <p>
            As alterações só entram em vigor ao publicar. Dono da demanda:
            responsável atual. Saúde permanece somente consulta externa.
          </p>
          <h3>Pessoas e acessos</h3>
          {data.users.map((u: any) => {
            const g = config.grants[u.id] ?? {
              access: false,
              create: false,
              edit: false,
              complete: false,
              share: false,
              transfer: false,
              send: false,
              readAll: false,
              profile: "advisor",
            };
            return (
              <details key={u.id}>
                <summary>
                  {u.name}
                  {!u.active ? " · desativado" : ""}
                </summary>
                <Field label="Padrão de perfil">
                  <select
                    value={g.profile}
                    onChange={(e) => {
                      const role = e.target.value as DemandGrant["profile"];
                      setConfig({
                        ...config,
                        grants: {
                          ...config.grants,
                          [u.id]: {
                            ...g,
                            profile: role,
                            access: true,
                            create: true,
                            edit: role !== "councillor",
                            complete: role !== "councillor",
                            share: role !== "councillor",
                            transfer: false,
                            send: role === "councillor",
                            readAll: role === "councillor",
                          },
                        },
                      });
                    }}
                  >
                    <option value="advisor">Assessor</option>
                    <option value="chief">Chefe de gabinete</option>
                    <option value="councillor">
                      Vereador · consulta e envio
                    </option>
                  </select>
                </Field>
                {Object.entries(grantNames).map(([key, label]) => (
                  <label className="demand-user-option" key={key}>
                    <input
                      type="checkbox"
                      disabled={
                        g.profile === "councillor" &&
                        ["edit", "complete", "share", "transfer"].includes(key)
                      }
                      checked={g[key as keyof DemandGrant] === true}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          grants: {
                            ...config.grants,
                            [u.id]: { ...g, [key]: e.target.checked },
                          },
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </details>
            );
          })}
          <h3>Tipos e documentos</h3>
          {config.types.map((t, i) => (
            <details key={t.id}>
              <summary>
                {t.name}
                {t.active ? "" : " · inativo"}
              </summary>
              <Field label="Nome do tipo">
                <input
                  value={t.name}
                  onChange={(e) => typeChange(i, { name: e.target.value })}
                />
              </Field>
              <label className="demand-user-option">
                <input
                  type="checkbox"
                  checked={t.active}
                  onChange={(e) => typeChange(i, { active: e.target.checked })}
                />
                Ativo para novos registros
              </label>
              <Field label="Subtipos (um por linha)">
                <textarea
                  value={t.subtypes.map((s) => s.name).join("\n")}
                  onChange={(e) =>
                    typeChange(i, {
                      subtypes: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map(
                          (name) =>
                            t.subtypes.find((s) => s.name === name) ?? {
                              id: crypto.randomUUID(),
                              name,
                              active: true,
                            },
                        ),
                    })
                  }
                />
              </Field>
              <label className="demand-user-option">
                <input
                  type="checkbox"
                  checked={t.subtypeRequired}
                  onChange={(e) =>
                    typeChange(i, { subtypeRequired: e.target.checked })
                  }
                />
                Subtipo obrigatório
              </label>
              <label className="demand-user-option">
                <input
                  type="checkbox"
                  checked={t.documents !== null}
                  onChange={(e) =>
                    typeChange(i, { documents: e.target.checked ? [] : null })
                  }
                />
                Exigências documentais definidas
              </label>
              {t.documents !== null && (
                <Field label="Documentos obrigatórios (um por linha; vazio significa nenhum)">
                  <textarea
                    value={t.documents.map((d) => d.name).join("\n")}
                    onChange={(e) =>
                      typeChange(i, {
                        documents: e.target.value
                          .split("\n")
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map(
                            (name) =>
                              t.documents?.find((d) => d.name === name) ?? {
                                id: crypto.randomUUID(),
                                name,
                              },
                          ),
                      })
                    }
                  />
                </Field>
              )}
              <Field label="Situações de andamento (uma por linha)">
                <textarea
                  value={t.statuses.join("\n")}
                  onChange={(e) =>
                    typeChange(i, {
                      statuses: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Field>
              <Field label="Pontos por primeiro encerramento">
                <input
                  inputMode="decimal"
                  value={t.points ?? ""}
                  onChange={(e) =>
                    typeChange(i, { points: e.target.value || null })
                  }
                />
              </Field>
              <Field label="XP por primeiro encerramento">
                <input
                  inputMode="decimal"
                  value={t.xp ?? ""}
                  onChange={(e) =>
                    typeChange(i, { xp: e.target.value || null })
                  }
                />
              </Field>
            </details>
          ))}
          <Button
            onClick={() =>
              setConfig({
                ...config,
                types: [
                  ...config.types,
                  {
                    id: crypto.randomUUID(),
                    name: "Novo tipo",
                    active: true,
                    subtypes: [],
                    subtypeRequired: false,
                    documents: null,
                    statuses: ["Pendente", "Em andamento"],
                    points: null,
                    xp: null,
                  },
                ],
              })
            }
          >
            Adicionar tipo
          </Button>
          <Field label="Política de divisão dos pontos e XP">
            <select
              value={config.rewardPolicy}
              onChange={(e) =>
                setConfig({
                  ...config,
                  rewardPolicy: e.target.value as DemandConfig["rewardPolicy"],
                })
              }
            >
              <option value="pending">
                Ainda não definida · nenhuma recompensa confirmada
              </option>
              <option value="exact-rational">
                Divisão exata igual, sem arredondamento no armazenamento
              </option>
            </select>
          </Field>
          <p>
            Na divisão exata, cada participante recebe a mesma fração do total;
            nenhum resto é descartado. Valores em branco ficam pendentes.
            Alterações não recalculam recompensas anteriores.
          </p>
          <Field label="Máximo de arquivos por demanda">
            <input
              type="number"
              min={1}
              max={25}
              value={config.maxFiles}
              onChange={(e) =>
                setConfig({ ...config, maxFiles: Number(e.target.value) })
              }
            />
          </Field>
          <Field label="Tamanho máximo por arquivo (MB)">
            <input
              type="number"
              min={1}
              max={20}
              value={config.maxUploadMb}
              onChange={(e) =>
                setConfig({ ...config, maxUploadMb: Number(e.target.value) })
              }
            />
          </Field>
          <details>
            <summary>Prévia e impacto</summary>
            <p>
              {Object.values(config.grants).filter((g) => g.access).length}{" "}
              usuários com acesso; {config.types.filter((t) => t.active).length}{" "}
              tipos ativos. Consulta global não concede edição global.
              Recompensas consolidadas são preservadas. Configuração ainda não
              publicada.
            </p>
          </details>
          <div className="actions">
            <Button onClick={() => void save(false)}>Salvar rascunho</Button>
            <Button kind="primary" onClick={() => void save(true)}>
              Publicar
            </Button>
          </div>
          <details>
            <summary>Versões e restauração</summary>
            {data.versions.map((v: any) => (
              <p key={v.revision}>
                Versão {v.revision} · {date(v.at)}{" "}
                <Button
                  onClick={() => {
                    setConfig(v.config);
                    setMessage(
                      "Versão carregada no rascunho. Revise antes de publicar.",
                    );
                  }}
                >
                  Carregar versão
                </Button>
              </p>
            ))}
          </details>
        </fieldset>
      )}
    </Modal>
  );
}
